import axios, { AxiosError } from "axios";
import { instanceToPlain, plainToInstance } from "class-transformer";
import { ConsentsDto, } from "healthex-common/consent.dtos";
import { PatientConsentsByStudyDto, StudyPatientConsentTotalsResult, } from "healthex-common/patient.consent.dtos";
import { CheckPatientsExistsInput, } from "healthex-common/recruitment.dtos";
import { err, voidResult } from "healthex-common/result";
import { authenticatedApiConfig, generateURL } from "./BaseApiService";
import { i18nGetCurrentLanguage } from "./i18next";
export class PatientService {
    constructor(authenticationProvider, environment) {
        this.authenticationProvider = authenticationProvider;
        this.environment = environment;
    }
    async requestEnrollmentConsent(studyId, studyOrgId, requestConsentInput) {
        await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:studyOrgId/studies/:studyId/request-enrollment-consent`, {
            studyOrgId,
            studyId,
        }), requestConsentInput, authenticatedApiConfig(this.authenticationProvider));
    }
    async updateHealthRecords(studyId, studyOrgId, requestConsentInput) {
        await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:studyOrgId/studies/:studyId/$update-health-records`, {
            studyOrgId,
            studyId,
        }), requestConsentInput, authenticatedApiConfig(this.authenticationProvider));
    }
    async revokeAllPatientConsents(patientId, revokeAllConsentsInput) {
        await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consents/$revokeAll`, {
            patientId,
        }), instanceToPlain(revokeAllConsentsInput), authenticatedApiConfig(this.authenticationProvider));
    }
    async getPatientStudyConsent(patientId, studyId, proofOfConsentPdf) {
        const url = generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consents/study/:studyId`, {
            patientId,
            studyId,
        });
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider, { proofOfConsentPdf }));
        return response.data;
    }
    async getFaq() {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/patient-faq`, {}));
        return response.data;
    }
    async postSearchForStudyConsents(patientId, input, start, end) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consents/study/search`, {
            patientId,
        }), input, authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return { total: response.data.total, results: plainToInstance(ConsentsDto, response.data.results) };
    }
    async postSearchForStudyPendingConsents(patientId, input, start, end) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/pending-consents/study/search`, {
            patientId,
        }), input, authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return { total: response.data.total, results: plainToInstance(PatientConsentsByStudyDto, response.data.results) };
    }
    async postSearchForPatientStudyConsents(patientId, input, start, end) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/patient-consents/study/search`, {
            patientId,
        }), input, authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return { total: response.data.total, results: plainToInstance(PatientConsentsByStudyDto, response.data.results) };
    }
    async postSearchForPatientStudyConsentsTotals(patientId, input, start, end) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/patient-consents/study/search/totals`, {
            patientId,
        }), input, authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return plainToInstance(StudyPatientConsentTotalsResult, response.data);
    }
    async updateStudyConsent(patientId, studyId, studyVersionId, consentType, consentUpdate) {
        consentUpdate.language = i18nGetCurrentLanguage();
        await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consents/study/:studyId/:studyVersionId/:consentType`, {
            patientId,
            studyId,
            studyVersionId,
            consentType,
        }), instanceToPlain(consentUpdate), authenticatedApiConfig(this.authenticationProvider));
    }
    async updateStudyConsentBatch(patientId, studyId, studyVersionId, consentUpdateBatch) {
        consentUpdateBatch.update.language = i18nGetCurrentLanguage();
        await axios.patch(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consents/study/:studyId/:studyVersionId`, {
            patientId,
            studyId,
            studyVersionId,
        }), instanceToPlain(consentUpdateBatch), authenticatedApiConfig(this.authenticationProvider));
    }
    async updateIASConsent(patientId, consentType, consentUpdate) {
        consentUpdate.language = i18nGetCurrentLanguage();
        await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consents/ias/:consentType`, {
            patientId,
            consentType,
        }), instanceToPlain(consentUpdate), authenticatedApiConfig(this.authenticationProvider));
    }
    async getPatientPreferences(patientId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/preferences`, {
            patientId,
        }), authenticatedApiConfig(this.authenticationProvider));
        return response.data === "" ? null : response.data;
    }
    async updatePatientPreferences(patientId, preferencesUpdate) {
        await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/preferences`, {
            patientId,
        }), preferencesUpdate, authenticatedApiConfig(this.authenticationProvider));
    }
    async hasPatientConsentedToStudy(patientId, studyId, consentType, includeConsentRecord = false) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consented/study/:studyId/:consentType`, {
            patientId,
            studyId,
            consentType,
        }), {
            ...authenticatedApiConfig(this.authenticationProvider),
            params: {
                ...(includeConsentRecord && { includeConsentRecord: true }),
            },
        });
        return response.data;
    }
    async hasPatientConsentedToIAS(patientId, consentType, includeConsentRecord = false) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consented/ias/:consentType`, {
            patientId,
            consentType,
        }), {
            ...authenticatedApiConfig(this.authenticationProvider),
            params: {
                ...(includeConsentRecord && { includeConsentRecord: true }),
            },
        });
        return response.data;
    }
    async getConsentFlowProgress(orgId, patientId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consent-flow-progress`, {
            orgId,
            patientId,
        }), input, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateConsentFlowProgress(orgId, patientId, update) {
        var _a, _b, _c;
        try {
            await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consent-flow-progress`, {
                orgId,
                patientId,
            }), update, authenticatedApiConfig(this.authenticationProvider));
            return voidResult();
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
            }
            throw e;
        }
    }
    async addPatients(projectId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/v1/projects/:projectId/patients`, {
            projectId,
        }), input, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async checkForDuplicates(projectId, studyOrgId, patients) {
        if (patients.length === 0) {
            return [];
        }
        const input = new CheckPatientsExistsInput({
            patients: patients.map((p) => ({ email: p.email, phone: p.phone })),
        });
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:studyOrgId/projects/:projectId/patients/exists`, {
            studyOrgId,
            projectId,
        }), input, authenticatedApiConfig(this.authenticationProvider));
        const result = response.data;
        return result.results;
    }
    async previewNotification(projectId, organizationId, request) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:organizationId/projects/:projectId/patients-notification-preview`, {
            organizationId,
            projectId,
        }), request, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async deleteAccount(patientId, input) {
        await axios.delete(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/delete-account`, {
            patientId,
        }), { ...authenticatedApiConfig(this.authenticationProvider), data: input });
    }
    async updatePatient(patientId, input) {
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/updatePatient`, {
            patientId,
        }), input, authenticatedApiConfig(this.authenticationProvider));
        return response.status === 200;
    }
}
